'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';

function getCurrentSearchParams(fallback: string): URLSearchParams {
  if (typeof window !== 'undefined' && window.location.search) {
    return new URLSearchParams(window.location.search);
  }

  return new URLSearchParams(fallback);
}

export function useSearchQuery(): [URLSearchParams, (obj: Record<string, string | string[] | null>) => void] {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const searchParamsKey = searchParams.toString();

  const query = useMemo(() => new URLSearchParams(searchParamsKey), [searchParamsKey]);

  const updateSearchQuery = useCallback(
    (obj: Record<string, string | string[] | null>) => {
      const next = getCurrentSearchParams(searchParamsKey);

      Object.entries(obj).forEach(([key, val]) => {
        if (val === null) {
          next.delete(key);
        } else if (Array.isArray(val)) {
          next.delete(key);
          val.forEach((v) => {
            next.append(key, v);
          });
        } else {
          next.set(key, val);
        }
      });

      const queryString = next.toString();
      const href = queryString ? `${pathname}?${queryString}` : pathname;

      router.replace(href, { scroll: false });
    },
    [pathname, router, searchParamsKey]
  );

  return [query, updateSearchQuery];
}
